// src/AnalyticsService.js
// Analítica propia: registra eventos en nuestra API (tabla app_events).
// Sin servicios de terceros — todo el dato queda en nuestro MySQL.
//
// Eventos automáticos:
//   - session_start (source: 'direct' | 'notification', platform: web/android/ios)
//
// Eventos custom: llama a trackCustomEvent('lo_que_sea', { datos }) desde
// cualquier componente para medir lo que te apetezca.
import { Capacitor } from '@capacitor/core';
import { TrackEvent } from './Backend/ApiCalls';
import { getActiveProfileToken } from './utils/profileStorage';

// Una "sesión" nueva como mucho cada 30 min (evita inflar el contador con
// cambios de pestaña / foregrounds rápidos de la app).
const SESSION_GAP_MS = 30 * 60 * 1000;
// Margen para detectar si la app se abrió desde una notificación: el click
// de OneSignal llega justo después del arranque. Hasta entonces no enviamos
// la sesión 'direct'.
const NOTIFICATION_GRACE_MS = 2500;

const LAST_TRACK_KEY = 'analytics-last-session-track';

let pendingDirectTimer = null;
let sessionAlreadySent = false;

const hasToken = () => Boolean(getActiveProfileToken());

const getPlatform = () => {
  try {
    return Capacitor.getPlatform(); // 'web' | 'android' | 'ios'
  } catch {
    return 'web';
  }
};

const enoughTimeSinceLastSession = () => {
  const last = Number(localStorage.getItem(LAST_TRACK_KEY) || 0);
  return Date.now() - last > SESSION_GAP_MS;
};

const sendSession = async (source, eventData = null) => {
  if (!hasToken()) return;

  sessionAlreadySent = true;
  localStorage.setItem(LAST_TRACK_KEY, String(Date.now()));

  try {
    await TrackEvent({
      eventType: 'session_start',
      platform: getPlatform(),
      source,
      eventData,
    });
  } catch (error) {
    console.warn('Analytics: no se pudo registrar la sesión.', error);
  }
};

// Llamar UNA vez al arrancar la app. Espera un pequeño margen por si el
// arranque viene de un click en notificación (en ese caso gana 'notification').
export const initSessionTracking = () => {
  if (sessionAlreadySent || !enoughTimeSinceLastSession()) return;

  pendingDirectTimer = window.setTimeout(() => {
    pendingDirectTimer = null;
    if (!sessionAlreadySent && enoughTimeSinceLastSession()) {
      sendSession('direct');
    }
  }, NOTIFICATION_GRACE_MS);

  // Si el usuario vuelve a la app/pestaña tras >30 min, cuenta como sesión nueva.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && enoughTimeSinceLastSession()) {
      sessionAlreadySent = false;
      sendSession('direct');
    }
  });
};

// Llamar desde el handler de click de notificación (OneSignal), pasándole
// la notificación normalizada para saber POR CUÁL ha entrado el usuario.
export const markNotificationOpen = (notification = null) => {
  if (pendingDirectTimer) {
    window.clearTimeout(pendingDirectTimer);
    pendingDirectTimer = null;
  }

  // Una apertura por notificación siempre se registra (aunque haya habido
  // una sesión hace poco): es justo el dato que queremos medir.
  sessionAlreadySent = false;
  sendSession('notification', notification ? {
    notificationId: notification.notificationId || notification.id || null,
    title: notification.title || null,
    route: notification.route || null,
  } : null);
};

// Para medir cualquier cosa custom en el futuro, por ejemplo:
//   trackCustomEvent('descarga_acta', { acta: 'enero-2026' });
//   trackCustomEvent('ver_galeria');
export const trackCustomEvent = async (eventType, eventData = null) => {
  if (!hasToken() || !eventType) return;
  try {
    await TrackEvent({
      eventType,
      platform: getPlatform(),
      source: 'custom',
      eventData,
    });
  } catch (error) {
    console.warn(`Analytics: no se pudo registrar el evento ${eventType}.`, error);
  }
};
